// Community Service Layer
// This service provides integration points for Supabase backend
// Currently uses mock implementations for frontend functionality

import {
  ReviewSubmission,
  PhotoUpload,
  PhotoRecord,
  ContentReport,
  Meetup,
  WalkingBuddyProfile,
  WalkingBuddyMatch,
  Message,
  Conversation,
  UserProfile,
  WeatherData,
  NotificationPreferences,
} from '../types/community';
import type { Review } from '../types';

class CommunityService {
  // ============================================
  // REVIEWS
  // ============================================

  async submitReview(review: ReviewSubmission): Promise<Review> {
    // TODO: Replace with Supabase integration
    // const { data, error } = await supabase
    //   .from('reviews')
    //   .insert(review)
    //   .select()
    //   .single();

    // Mock implementation
    await this.delay(1000);

    const newReview: Review = {
      id: `r-${Date.now()}`,
      parkId: review.park_id,
      userId: review.user_id,
      userName: 'Current User', // TODO: Get from auth context
      rating: review.rating,
      date: new Date().toISOString().split('T')[0],
      content: review.content,
      images: [],
      helpful: 0,
      petInfo: review.pet_id ? { name: 'Pet Name', size: 'Medium' } : undefined,
    };

    return newReview;
  }

  async updateReview(_reviewId: string, _content: string): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  async deleteReview(_reviewId: string): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  // ============================================
  // HELPFUL VOTES
  // ============================================

  async toggleHelpfulVote(
    _reviewId: string,
    _userId: string
  ): Promise<boolean> {
    // TODO: Supabase integration with RLS
    // Check if vote exists, toggle accordingly
    await this.delay(300);
    return true; // Returns new vote state
  }

  async getHelpfulCount(_reviewId: string): Promise<number> {
    // TODO: Supabase integration
    await this.delay(200);
    return 0;
  }

  async hasUserVoted(_reviewId: string, _userId: string): Promise<boolean> {
    // TODO: Supabase integration
    await this.delay(200);
    return false;
  }

  // ============================================
  // PHOTO UPLOADS
  // ============================================

  async uploadPhoto(photo: PhotoUpload): Promise<PhotoRecord> {
    // TODO: Supabase Storage integration
    // 1. Upload to storage bucket
    // 2. Create database record
    // 3. Set status to 'pending' for moderation

    await this.delay(2000);

    const photoRecord: PhotoRecord = {
      id: `photo-${Date.now()}`,
      park_id: photo.park_id,
      user_id: photo.user_id,
      username: 'Current User',
      url: photo.preview_url,
      caption: photo.caption,
      status: 'pending',
      uploaded_at: new Date().toISOString(),
    };

    return photoRecord;
  }

  async getPhotosByPark(_parkId: string): Promise<PhotoRecord[]> {
    // TODO: Supabase integration
    // Only return approved photos for public view
    await this.delay(500);
    return [];
  }

  async deletePhoto(_photoId: string): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  // ============================================
  // CONTENT REPORTS
  // ============================================

  async reportContent(
    _report: Omit<ContentReport, 'id' | 'status' | 'created_at'>
  ): Promise<void> {
    // TODO: Supabase integration
    // Create report record
    // Trigger moderation notification
    await this.delay(500);
  }

  // ============================================
  // USER PROFILES
  // ============================================

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    // TODO: Supabase integration
    await this.delay(500);

    // Mock profile
    return {
      id: userId,
      username: 'johndoe',
      full_name: 'John Doe',
      avatar_url: undefined,
      bio: 'Dog lover and park enthusiast',
      location: 'San Francisco, CA',
      pets: [],
      reviews_count: 12,
      photos_count: 24,
      meetups_hosted: 3,
      meetups_joined: 8,
      created_at: '2024-01-01',
      privacy_settings: {
        show_pets: true,
        show_location: true,
        show_activity: true,
      },
    };
  }

  async updateProfile(
    _userId: string,
    _updates: Partial<UserProfile>
  ): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  // ============================================
  // MEETUPS
  // ============================================

  async createMeetup(
    meetup: Omit<
      Meetup,
      'id' | 'current_participants' | 'participants' | 'status' | 'created_at'
    >
  ): Promise<Meetup> {
    // TODO: Supabase integration
    await this.delay(1000);

    const newMeetup: Meetup = {
      ...meetup,
      id: `meetup-${Date.now()}`,
      current_participants: 1,
      status: 'upcoming',
      participants: [
        {
          user_id: meetup.organizer_id,
          username: meetup.organizer_name,
          avatar_url: meetup.organizer_avatar,
          status: 'attending',
          joined_at: new Date().toISOString(),
        },
      ],
      created_at: new Date().toISOString(),
    };

    return newMeetup;
  }

  async getMeetupsByPark(_parkId: string): Promise<Meetup[]> {
    // TODO: Supabase integration
    await this.delay(500);
    return [];
  }

  async rsvpMeetup(
    _meetupId: string,
    _userId: string,
    _status: 'attending' | 'maybe' | 'declined'
  ): Promise<void> {
    // TODO: Supabase integration
    // Check capacity before confirming
    await this.delay(500);
  }

  async cancelMeetup(_meetupId: string): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  // ============================================
  // WALKING BUDDY
  // ============================================

  async updateWalkingBuddyProfile(
    _profile: WalkingBuddyProfile
  ): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  async getWalkingBuddyMatches(_userId: string): Promise<WalkingBuddyMatch[]> {
    // TODO: Implement matching algorithm
    // Consider:
    // - Location proximity
    // - Pet compatibility (size, energy)
    // - Schedule overlap
    // - Preferred parks
    await this.delay(1000);

    // No placeholder data: return empty until real backend matching exists.
    return [];
  }

  // ============================================
  // MESSAGING
  // ============================================

  async getConversations(_userId: string): Promise<Conversation[]> {
    // TODO: Supabase integration with realtime
    await this.delay(500);
    return [];
  }

  async getMessages(_conversationId: string): Promise<Message[]> {
    // TODO: Supabase integration with realtime
    await this.delay(500);
    return [];
  }

  async sendMessage(
    conversationId: string,
    senderId: string,
    content: string
  ): Promise<Message> {
    // TODO: Supabase integration with realtime
    await this.delay(300);

    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      conversation_id: conversationId,
      sender_id: senderId,
      sender_name: 'Current User',
      content,
      created_at: new Date().toISOString(),
      read: false,
    };

    return newMessage;
  }

  async markAsRead(_conversationId: string, _userId: string): Promise<void> {
    // TODO: Supabase integration
    await this.delay(200);
  }

  async startConversation(participants: string[]): Promise<Conversation> {
    // TODO: Supabase integration
    await this.delay(500);

    const newConversation: Conversation = {
      id: `conv-${Date.now()}`,
      participants,
      participant_names: ['User 1', 'User 2'],
      participant_avatars: [],
      unread_count: 0,
      created_at: new Date().toISOString(),
    };

    return newConversation;
  }

  // ============================================
  // NOTIFICATIONS
  // ============================================

  async getNotificationPreferences(
    _userId: string
  ): Promise<NotificationPreferences> {
    // TODO: Supabase integration
    await this.delay(300);

    return {
      push_enabled: false,
      email_enabled: true,
      notifications: {
        messages: true,
        meetups: true,
        reviews: true,
        safety_alerts: true,
        walking_buddy: true,
        community_updates: false,
      },
    };
  }

  async updateNotificationPreferences(
    _userId: string,
    _preferences: NotificationPreferences
  ): Promise<void> {
    // TODO: Supabase integration
    await this.delay(500);
  }

  async requestPushPermission(): Promise<boolean> {
    // TODO: Web Push API integration
    if (!('Notification' in window)) {
      return false;
    }

    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  // ============================================
  // WEATHER
  // ============================================

  async getWeather(_lat: number, _lng: number): Promise<WeatherData> {
    // TODO: OpenWeather API integration
    await this.delay(500);

    // Mock weather data
    return {
      temperature: 72,
      conditions: 'Partly Cloudy',
      icon: '⛅',
      humidity: 65,
      wind_speed: 8,
      walking_score: 'excellent',
      last_updated: new Date().toISOString(),
    };
  }

  // ============================================
  // GEOLOCATION
  // ============================================

  async getCurrentLocation(): Promise<{ lat: number; lng: number } | null> {
    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          resolve(null);
        }
      );
    });
  }

  // ============================================
  // UTILITY
  // ============================================

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const communityService = new CommunityService();
